import React, { useMemo, useCallback } from 'react'
import styled from '@emotion/styled'
import isNil from 'lodash/isNil'
import {
  type GridColumns,
  fontSize,
  colors,
  type GridColumn,
} from '@retailer-platform/shared-components'
import { type Moment } from 'moment'
import { useCreateHref } from 'utils/routing/navigation.hooks'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import {
  type StoreMetric,
  type ShiftsQueryOptions,
} from 'domains/aggregate-store-performance/types/AggregateStorePerformance.types'
import {
  momentToQueryStringDate,
  hasDedicatedRates,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.helpers'
import { trackStoreNameClicked } from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.tracking'
import { useRetailerId } from 'utils/routing/params.hooks'
import { createBasicColumn } from './createBasicColumn'

const StoreName = styled.span({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: fontSize.X12,
  lineHeight: '13px',
})

const Link = styled.a({
  cursor: 'pointer',
  textDecoration: 'underline',
  color: colors.GRAYSCALE.X70,
})

const getStoreLinkQuery = (
  storeId: string,
  date: Moment,
  isWeek: boolean,
  showEditButton: boolean
): ShiftsQueryOptions => {
  const queryDate = momentToQueryStringDate(date)
  const query: ShiftsQueryOptions = {
    warehouse_location_id: storeId, // eslint-disable-line @typescript-eslint/naming-convention
  }

  if (showEditButton) {
    query.date = queryDate

    if (isWeek) {
      query.week = queryDate
    }
  }

  return query
}

interface useTableColumnsOptions {
  showEditButton?: boolean
  data: StoreMetric[]
  date: Moment
  isWeek: boolean
}

export const useTableColumns = ({ showEditButton, data, date, isWeek }: useTableColumnsOptions) => {
  const messages = useDomainMessages({
    stores: 'aggregateStorePerformanceDomain.storeMetricsTable.storeName',
    hoursScheduled: 'aggregateStorePerformanceDomain.storeMetricsTable.hoursScheduled',
    hoursRecommended: 'aggregateStorePerformanceDomain.storeMetricsTable.hoursRecommended',
    scheduleGaps: 'aggregateStorePerformanceDomain.storeMetricsTable.scheduleGaps',
    edit: 'aggregateStorePerformanceDomain.storeMetricsTable.edit',
    matchRate: 'aggregateStorePerformanceDomain.storeMetricsTable.matchRate',
    dedicatedMatchRate: 'aggregateStorePerformanceDomain.storeMetricsTable.dedicatedMatchRate',
    utilization: 'aggregateStorePerformanceDomain.storeMetricsTable.utilization',
    dedicatedUtilization: 'aggregateStorePerformanceDomain.storeMetricsTable.dedicatedUtilization',
    fiveHourAvailability: 'aggregateStorePerformanceDomain.storeMetricsTable.fiveHourAvailability',
    partnerPickOrders: 'aggregateStorePerformanceDomain.storeMetricsTable.partnerPickOrders',
    fullServiceShopperOrders:
      'aggregateStorePerformanceDomain.storeMetricsTable.fullServiceShopperOrders',
    partnerPickOrdersTooltipTitle:
      'aggregateStorePerformanceDomain.storeMetricsTable.partnerPickOrdersTooltipTitle',
    partnerPickOrdersTooltipContent:
      'aggregateStorePerformanceDomain.storeMetricsTable.partnerPickOrdersTooltipContent',
    fullServiceShopperOrdersTooltipTitle:
      'aggregateStorePerformanceDomain.storeMetricsTable.fullServiceShopperOrdersTooltipTitle',
    fullServiceShopperOrdersTooltipContent:
      'aggregateStorePerformanceDomain.storeMetricsTable.fullServiceShopperOrdersTooltipContent',
    fullServiceShopperOrdersTooltipDisclaimer:
      'aggregateStorePerformanceDomain.storeMetricsTable.fullServiceShopperOrdersTooltipDisclaimer',
  })

  const retailerId = useRetailerId()

  const columns: GridColumns<StoreMetric> = useMemo(() => {
    const hasFullServiceShopperOrders =
      data.length &&
      !data.every(
        storeMetric =>
          isNil(storeMetric.fullServiceShopperOrders) || storeMetric.fullServiceShopperOrders === 0
      )
    const storesHeaderTitle = `${messages.stores} (${data.length})`

    const storeName: GridColumn<StoreMetric> = {
      id: 'store-name',
      Header: storesHeaderTitle,
      accessor: ({ storeName }) => storeName,
      Cell: ({
        row: {
          original: { storeName, storeId },
        },
      }) => {
        const query: ShiftsQueryOptions = getStoreLinkQuery(storeId, date, isWeek, showEditButton)
        const shiftsURL = useCreateHref('shopper-shifts', {}, query)

        const handleClick = useCallback(() => {
          trackStoreNameClicked({ storeId, retailerId })
        }, [storeId])

        return (
          <StoreName>
            {storeId ? (
              <Link href={shiftsURL} onClick={handleClick}>
                {storeName}
              </Link>
            ) : (
              storeName
            )}
          </StoreName>
        )
      },
      isSortable: true,
      sortType: 'basic',
    }

    const hoursScheduled = createBasicColumn({
      id: 'hours-scheduled',
      header: messages.hoursScheduled,
      field: 'hoursScheduled',
    })

    const hoursRecommended = createBasicColumn({
      id: 'hours-recommended',
      header: messages.hoursRecommended,
      field: 'hoursRecommended',
    })

    const scheduleGaps = createBasicColumn({
      id: 'schedule-gaps',
      header: messages.scheduleGaps,
      field: 'scheduleGaps',
      noValueIndicator: 0,
    })

    const matchRate = createBasicColumn({
      id: 'match-rate',
      header: messages.matchRate,
      field: 'matchRate',
    })

    const dedicatedMatchRate = createBasicColumn({
      id: 'dedicated-match-rate',
      header: messages.dedicatedMatchRate,
      field: 'dedicatedMatchRate',
    })

    const utilization = createBasicColumn({
      id: 'utilization',
      header: messages.utilization,
      field: 'utilization',
    })

    const dedicatedUtilization = createBasicColumn({
      id: 'dedicated-utilization',
      header: messages.dedicatedUtilization,
      field: 'dedicatedUtilization',
    })

    const fiveHourAvailability = createBasicColumn({
      id: 'five-hour-availability',
      header: messages.fiveHourAvailability,
      field: 'fiveHourAvailability',
    })

    const partnerPickOrders = createBasicColumn({
      id: 'partner-pick-orders',
      field: 'partnerPickOrders',
      header: messages.partnerPickOrders,
      tooltip: {
        title: messages.partnerPickOrdersTooltipTitle,
        content: messages.partnerPickOrdersTooltipContent,
      },
    })

    const fullServiceShopperOrders = createBasicColumn({
      id: 'full-service-shopper-orders',
      field: 'fullServiceShopperOrders',
      header: messages.fullServiceShopperOrders,
      tooltip: {
        title: messages.fullServiceShopperOrdersTooltipTitle,
        content: messages.fullServiceShopperOrdersTooltipContent,
        disclaimer: messages.fullServiceShopperOrdersTooltipDisclaimer,
      },
    })

    const useDedicatedRates = hasDedicatedRates(data)

    const columns = [
      storeName,
      hoursScheduled,
      hoursRecommended,
      scheduleGaps,
      useDedicatedRates ? dedicatedMatchRate : matchRate,
      useDedicatedRates ? dedicatedUtilization : utilization,
      fiveHourAvailability,
      partnerPickOrders,
    ]

    if (hasFullServiceShopperOrders) {
      columns.push(fullServiceShopperOrders)
    }

    return columns
  }, [showEditButton, date, isWeek, data, messages, retailerId])

  return columns
}
